import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "../../common/components/Button";
import Card from "../../common/components/Card";
import CustomForm from "../../common/components/CustomForm";
import { customCtrlTypeSupported } from "../../common/components/DataTable";
import DateInput from "../../common/components/DateInput";
import { Col, Container, Row } from "../../common/components/Grid";
import Icon from "../../common/components/Icon";
import Label from "../../common/components/Label";
import Select from "../../common/components/Select";
import Signature from "../../common/components/Signature";
import TextInput from "../../common/components/TextInput";
import TextareaInput from "../../common/components/TextareaInput";
import TimeInput from "../../common/components/TimeInput";
import useClientConfig from "../../common/hooks/clientConfigHook";
import { now } from "../../common/utils/CDate";
import { ConfirmDialog, ShowMessageDialog } from "../../common/utils/dialogUtlities";
import { safeEval } from "../../common/utils/safeEval";
import { useSelectedPCR } from "../../utils/CADStateProvider";
import EpcrAcuityDropdown from "./EpcrAcuityDropdown";
import { useEpcrEditorEpcr, useEpcrEditorIntent } from "./EpcrContext";
import EpcrDispositionDropdown from "./EpcrDispositionDropdown";
import PropTypes from 'prop-types';
import { useEpcrDispositions } from "../../reducers/epcrDispositionSlice";
import EpcrComplaintInput from "./EpcrComplaintInput";
import { deepClone } from "../../common/utils/objectUtilities";

const EpcrOutcome = ({ disabled = false}) => {
    const { t } = useTranslation();
    const clientConfig = useClientConfig();
    const { epcr, site, level, type, update, history, meds, allergies, vitals, procedures, treatmentMeds } = useEpcrEditorEpcr();
    const dispositions = useEpcrDispositions();
    const intent = useEpcrEditorIntent();
    const { setSelectedPcrID } = useSelectedPCR();
    const [showErrorDialog, setShowErrorDialog] = useState(false);
    const [showConfirmFinalizeDialog, setShowConfirmFinalizeDialog] = useState(false);
    const [errorMessage, setErrorMessage] = useState();

    const finalizationValidation = useMemo(() => clientConfig.epcrFinalizationValidation ? safeEval(`(${clientConfig.epcrFinalizationValidation})`) : null, [clientConfig.epcrFinalizationValidation]);

    const discharge = () => {
        update('dischargetimestamp', new Date());
    }

    const confirmFinalize = () => {
        if (finalizationValidation) {
            let errors = window.validate({ ...epcr, history, meds, allergies, vitals, procedures, treatmentMeds }, finalizationValidation.pcr, {format: 'flat', fullMessages: false});
            if (errors?.length > 0) {
                let message = <>
                    <p>{t("epcrValidationErrorMessage")}:</p>
                    <ul> {errors?.map(e => <li>{e}</li>)}</ul>
                </>
                setErrorMessage(message);
                setShowErrorDialog(true);
                return;
            }
        }
        setShowConfirmFinalizeDialog(true);
    }

    const closeErrorDialog = () => {
        setErrorMessage();
        setShowErrorDialog(false);
    }
    

    const finalize = (doFinalize) => {
        if (doFinalize) {
            update("finalized", now().toISOString());
            setSelectedPcrID();
        }
        setShowConfirmFinalizeDialog(false);
    }

    const def = useMemo(() => {
        if (intent === 'search' && clientConfig.epcrResultFormSpec) {
            let def = deepClone(clientConfig.epcrResultFormSpec);
            def.forEach(section => {
                section.rows = section.rows.map(row => row.filter(cell => customCtrlTypeSupported(cell)));
                section.rows = section.rows.filter(row => row.length > 0);
            });
            def = def.filter(section => section.rows.length > 0);
            return def;
        }
        return clientConfig.epcrResultFormSpec;
    }, [intent, clientConfig.epcrResultFormSpec]);

    const accompaniedbyOptions = useMemo(() => [t('self'), t('family'), t('friend'), t('police'), t('ambulance')], [t])

    return <>
    <ShowMessageDialog showModal={showErrorDialog} title={t("validationErrors")} onClose={closeErrorDialog} message={errorMessage}></ShowMessageDialog>
    <ConfirmDialog showModal={showConfirmFinalizeDialog} title={t("finalizeEpcr", {id: epcr?.id})} onClose={finalize} message={t("confirmFinalizeEpcrMessage")}></ConfirmDialog>
    <Container fluid>
        <Card title={t('dischargeDestination')}>
            <Row>
                <Col md={4}><Label hideLabel={true} text={`${t("finalPrimaryImpression")}:`}></Label></Col>
                <Col md={8}><EpcrComplaintInput disabled={disabled} hideLabel={true} site={site} type={type} level={level} value={epcr?.finalassessment} placeholder={t("finalPrimaryImpression")} onChange={p => update('finalassessment', p)}></EpcrComplaintInput></Col>
            </Row>
            <Row>
                <Col md={4}><Label hideLabel={true} text={`${t("finalSecondaryImpression")}:`}></Label></Col>
                <Col md={8}><EpcrComplaintInput disabled={disabled} hideLabel={true} site={site} type={type} level={level} value={epcr?.finalassessment2} placeholder={t("finalSecondaryImpression")} onChange={p => update('finalassessment2', p)}></EpcrComplaintInput></Col>
            </Row>
            <Row>
                <Col md={4}><Label hideLabel={true} text={`${t("finalPatientAcuity")}:`}></Label></Col>
                <Col md={8}><EpcrAcuityDropdown hideLabel={true} acuity={epcr?.finalpatientacuity} placeholder={t('finalPatientAcuity')} disabled={disabled} onChange={a => update('finalpatientacuity', a)}></EpcrAcuityDropdown></Col>
            </Row>
            { dispositions?.length > 0 && <Row>
                <Col md={4}><Label hideLabel={true} text={`${t("dischargeDestination")}:`}></Label></Col>
                <Col md={8}><EpcrDispositionDropdown hideLabel={true} disposition={epcr?.disposition} disabled={disabled} onChange={a => update('disposition', a)}></EpcrDispositionDropdown></Col>
            </Row> }
        </Card>
        {clientConfig.enableEPCRInformationRelease && <Card title={t('informationRelease')}>
            <Row>
                <Col md={4}><Label hideLabel={true} text={`${t("informationRelease")}:`}></Label></Col>
                <Col md={8}><Select hideLabel={true} value={epcr?.inforelease} disabled={disabled} onChange={a => update('inforelease', a)} options={[{value: 1, label: t('yes')}, {value: 0, label: t('no')}]} labelField="label" valueField="value"></Select></Col>
            </Row>
            { intent === 'edit' && <Row>
                <Col md={4}></Col>
                <Col md={8}><Signature disabled={disabled} value={epcr?.infosignature} hideLabel={true} onChange={s => update('infosignature', s)}></Signature></Col>
            </Row> }
            <Row>
                <Col md={4}><Label hideLabel={true} text={`${t("name")}:`}></Label></Col>
                <Col md={8}><TextInput hideLabel={true} maxLength={128} disabled={disabled} value={epcr?.inforeleasename} onChange={n => update('inforeleasename', n)}></TextInput></Col>
            </Row>
            <Row>
                <Col md={4}><Label hideLabel={true} text={`${t("timeOfDay")}:`}></Label></Col>
                <Col md={4}><TimeInput hideLabel={true} value={epcr?.inforeleasetime} disabled={disabled} placeholder={t('timeOfDay')} onChange={t => update('inforeleasetime', t)}></TimeInput></Col>
                <Col md={4}><DateInput hideLabel={true} value={epcr?.inforeleasetime} disabled={disabled} placeholder={t('dateTime')} onChange={t => update('inforeleasetime', t)}></DateInput></Col>
            </Row>
        </Card>}
        {clientConfig.enableEPCRLegacyCrewDetails ? <Card title={t('primaryCrewMember')}>
            <Row>
                <Col md={4}><Label hideLabel={true} text={`${t("name")}:`}></Label></Col>
                <Col md={8}><TextInput hideLabel={true} maxLength={128} disabled={disabled} value={epcr?.dischargename} onChange={n => update('dischargename', n)}></TextInput></Col>
            </Row>
            <Row>
                <Col md={4}><Label hideLabel={true} text={`${t("idNumber")}:`}></Label></Col>
                <Col md={8}><TextInput hideLabel={true} maxLength={128} disabled={disabled} value={epcr?.dischargeid} onChange={n => update('dischargeid', n)}></TextInput></Col>
            </Row>
            <Row>
                <Col md={4}><Label hideLabel={true} text={`${t("title")}:`}></Label></Col>
                <Col md={8}><TextInput hideLabel={true} maxLength={128} disabled={disabled} value={epcr?.dischargetitle} onChange={n => update('dischargetitle', n)}></TextInput></Col>
            </Row>
            <Row>
                <Col md={4}><Label hideLabel={true} text={`${t("timeOfDay")}:`}></Label></Col>
                <Col md={disabled ? 8 : 6}><TimeInput hideLabel={true} value={epcr?.dischargetimestamp} disabled={disabled} onChange={t => update('dischargetimestamp', t)}></TimeInput></Col>
                {!disabled && intent === 'edit' ? <Col md={2}><Button variant="primary" onClick={discharge}>{t('dischargeVerb')}</Button></Col> : null}
            </Row>
            <Row>
                <Col md={4}><Label hideLabel={true} text={`${t("accompaniedBy")}:`}></Label></Col>
                <Col md={4}><Select hideLabel={true} value={epcr?.accompaniedby} placeholder={t('relationship')} disabled={disabled} onChange={a => update('accompaniedby', a)} options={accompaniedbyOptions}></Select></Col>
                <Col md={4}><TextInput hideLabel={true} placeholder={t('details')} maxLength={128} value={epcr?.accompanieddetails} disabled={disabled} onChange={d => update('accompanieddetails', d)}></TextInput></Col>
            </Row>

        </Card> : null }
        <Card title={t('adviceOrComments')}>
            <TextareaInput hideLabel={true} disabled={disabled} rows={8} maxLength={1024} value={epcr?.dischargedetails} onChange={a => update('dischargedetails', a)}></TextareaInput>
        </Card>
        {clientConfig.epcrResultFormSpec ? <CustomForm disabled={disabled} hideLabel={true} def={def} data={epcr?.eventcustomfields ?? {}} onChange={c => update('eventcustomfields', c)}></CustomForm> : null}
        {!epcr?.finalized && <Button size="lg" onClick={confirmFinalize} block><Icon intent="locked"></Icon> {t("finalizeRecord")}</Button>}
    </Container>
    </>
}

EpcrOutcome.propTypes = {
    type: PropTypes.string,
    site: PropTypes.string,
    level: PropTypes.string,
    disabled: PropTypes.bool
}

export default EpcrOutcome; 